import React, { useMemo } from 'react';
import { StyledResultTitle } from 'pages/search/SearchResult';
import { StyledLinkedResultSnippet } from 'components/common/search/LinkedResultSnippets';
import { IconFromType } from './IconFromType';
import { TrackedLink as Link } from 'components/common/TrackedLink';
import styled from 'styled-components';
import { PractitionerSearchEdge, Snippet } from '__generated__/graphql';
import { useAssetAccess } from 'components/asset-access';
import {
  usePractitionerDocumentPath,
  PractitionerDocumentLink,
} from 'features/link-helpers/PractitionerLink';
import { useSearchParams } from 'react-router-dom';

export interface PractitionerResultProps {
  result: PractitionerSearchEdge;
  noIcons?: boolean;
  className?: string;
}

export function PractitionerResult(props: PractitionerResultProps) {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('query');
  const {
    node: { contentfulKnowHowDocument },
    snippets,
  } = props.result;
  const {
    hasPractitionerAllAccess,
    hasPractitionerTrial,
    availableProductSlugs,
  } = useAssetAccess();

  const documentSlugs = useMemo(
    () =>
      contentfulKnowHowDocument &&
      (contentfulKnowHowDocument!.practiceAreasCollection?.items || []).map(
        practiceArea => practiceArea?.slug!,
      ),
    [contentfulKnowHowDocument],
  );

  const hasAccessToProduct = useMemo(
    () =>
      contentfulKnowHowDocument &&
      documentSlugs &&
      (availableProductSlugs?.some(r => documentSlugs.includes(r)) ||
        hasPractitionerAllAccess ||
        hasPractitionerTrial),
    [
      documentSlugs,
      availableProductSlugs,
      hasPractitionerAllAccess,
      hasPractitionerTrial,
      contentfulKnowHowDocument,
    ],
  );

  const type = contentfulKnowHowDocument?.practitionerDocumentType?.name || '';
  const slug = contentfulKnowHowDocument?.slug || '';
  const url = usePractitionerDocumentPath(slug, type || undefined, query);

  if (!contentfulKnowHowDocument) return null;

  return (
    <StyledPractitionerResult className={props.className || ''}>
      {!props.noIcons && (
        <span className="icon">
          <IconFromType type={type} />
        </span>
      )}
      <header>
        <StyledResultTitle>
          {hasAccessToProduct ? (
            <PractitionerDocumentLink slug={slug} doctype={type} query={query}>
              {contentfulKnowHowDocument.title}
            </PractitionerDocumentLink>
          ) : (
            <>
              <i className="fas fa-lock" />
              {contentfulKnowHowDocument.title}
            </>
          )}
        </StyledResultTitle>
        <section>
          <div>{contentfulKnowHowDocument.practitionerDocumentType?.name}</div>
          {!hasAccessToProduct && (
            <StyledOutOfPlanMessage>
              <span>Out-of-Plan</span> &nbsp;&nbsp;|&nbsp;&nbsp;
              <Link to="/account/purchases">How do I get access?</Link>
            </StyledOutOfPlanMessage>
          )}
        </section>
      </header>
      {type === 'Workflows' ? (
        <span>
          <PractitionerDocumentLink slug={slug} doctype={type}>
            {contentfulKnowHowDocument.description}
          </PractitionerDocumentLink>
        </span>
      ) : (
        <StyledLinkedResultSnippet>
          {(snippets || []).map((snippet, i) => (
            <PractitionerDocumentLink
              key={i}
              slug={slug}
              doctype={type}
              query={query}
              hash={snippet?.position || ''}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: snippet?.text || '',
                }}
              />
            </PractitionerDocumentLink>
          ))}
        </StyledLinkedResultSnippet>
      )}
    </StyledPractitionerResult>
  );
}

const StyledOutOfPlanMessage = styled.section`
  margin-left: 0.5em;
  flex: 1;
  span {
    color: var(--COLOR_ORANGEY_YELLOW);
    font-weight: 600;
  }
  a {
    font-weight: 600;
  }
`;

const StyledPractitionerResult = styled.li`
  span.icon {
    float: left;
    margin-left: -35px;
    display: inline;
  }
  span {
    display: inline;
    a,
    a:visited {
      display: block;
      color: inherit;
      text-decoration: none;
    }
  }
  & + & {
    margin-top: 34px;
  }

  header {
    h3 {
      .fa-lock {
        color: var(--COLOR_ORANGEY_YELLOW);
        margin-right: 0.5em;
      }
    }
    section {
      display: flex;
      margin-bottom: 10px;
      div {
        flex: 0 1 auto;
        font-size: 15px;
        font-weight: bold;
        &:before {
          content: '•';
          display: inline-block;
          margin: 0 0.5em;
        }
        &:first-child:before {
          display: none;
        }
      }
    }
  }
`;
